import axios, { AxiosRequestConfig } from "../axios.service";
import config from "../../_helpers/app.constants";
import { IFilterParameters } from "app/components/Dashboard/types";
import {
  IAverageSpendDay,
  IAverageSpendMonth,
  IAverageSpendPerPax,
  IAverageSpendPerDuration,
  IPurchasedItem,
  IService,
  IGuestsReportResult,
} from "./reporting.type";
import { headers } from "app/_helpers";
import { getUtcDateByVenueTimeZone } from "app/components/Dashboard/helper";

/**
 * Service to get the weekly/monthly statistics based on the from date, to date and venue id
 */
export class ReportingService {
  private static MapServices(services: IService[]): IService[] {
    return services.map((service: IService) => {
      return { ...service, spendInDollar: service.spend / 100 } as IService;
    });
  }

  /** Provides the weekly spend average statistics */
  static async getWeeklySpendAverage(
    filterParameters: IFilterParameters
  ): Promise<IAverageSpendDay[]> {
    try {
      console.log(`call to reportingService.getWeeklySpendAverage`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/average-spend?startDate=${filterParameters.fromDate}&endDate=${filterParameters.toDate}`;
      const avgSpends = await axios.get<IAverageSpendDay[]>(url, reqConfig);
      if (avgSpends.data && avgSpends.data.length > 0) {
        const result = avgSpends.data.map((item: IAverageSpendDay) => {
          return {
            ...item,
            spendInDollar: parseFloat((item.spend / 100).toFixed(2)),
            service: this.MapServices(item.service),
          } as IAverageSpendDay;
        });
        return result;
      }
    } catch (error) {
      console.warn(
        `call to reportingService.getPerSpendDurationAverage for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IAverageSpendDay[];
  }

  /** Provides the monthly spend average statistics */
  static async getMonthlySpendAverage(
    filterParameters: IFilterParameters
  ): Promise<IAverageSpendMonth[]> {
    try {
      console.log(`call to reportingService.getMonthlySpendAverage`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/average-spend-per-month?startDate=${filterParameters.fromDate}&endDate=${filterParameters.toDate}`;
      const avgSpendPerMonth = await axios.get<IAverageSpendMonth[]>(
        url,
        reqConfig
      );
      if (avgSpendPerMonth.data && avgSpendPerMonth.data.length > 0) {
        const result = avgSpendPerMonth.data.map((item: IAverageSpendMonth) => {
          return {
            ...item,
            spendInDollar: parseFloat((item.spend / 100).toFixed(2)),
            service: this.MapServices(item.service),
          } as IAverageSpendMonth;
        });
        return result;
      }
    } catch (error) {
      console.warn(
        `call to reportingService.getMonthlySpendAverage for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IAverageSpendMonth[];
  }

  /** Provides the monthly spend total statistics */
  static async getMonthlySpendTotal(
    filterParameters: IFilterParameters
  ): Promise<IAverageSpendMonth[]> {
    try {
      console.log(`call to reportingService.getMonthlySpendTotal`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/total-spend-per-month?startDate=${filterParameters.fromDate}&endDate=${filterParameters.toDate}`;
      const avgSpendPerDuration = await axios.get<IAverageSpendMonth[]>(
        url,
        reqConfig
      );
      if (avgSpendPerDuration.data && avgSpendPerDuration.data.length > 0) {
        const result = avgSpendPerDuration.data.map(
          (item: IAverageSpendMonth) => {
            return {
              ...item,
              service: this.MapServices(item.service),
            } as IAverageSpendMonth;
          }
        );
        return result;
      }
    } catch (error) {
      console.warn(
        `call to reportingService.getMonthlySpendTotal for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IAverageSpendMonth[];
  }

  /** Provides the per pax spend average statistics */
  static async getPerPaxSpendAverage(
    filterParameters: IFilterParameters
  ): Promise<IAverageSpendPerPax[]> {
    try {
      console.log(`call to reportingService.getPerPaxSpendAverage`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/average-spend-per-pax?startDate=${filterParameters.fromDate}&endDate=${filterParameters.toDate}`;
      const avgSpendPerPax = await axios.get<IAverageSpendPerPax[]>(
        url,
        reqConfig
      );
      if (avgSpendPerPax.data && avgSpendPerPax.data.length > 0) {
        const result = avgSpendPerPax.data.map((item: IAverageSpendPerPax) => {
          return {
            ...item,
            AverageSpendInDollar: item.averageSpend / 100,
          } as IAverageSpendPerPax;
        });
        return result;
      }
    } catch (error) {
      console.warn(
        `call to reportingService.getPerPaxSpendAverage for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IAverageSpendPerPax[];
  }

  /** Provides the per duration spend average statistics */
  static async getPerSpendDurationAverage(
    filterParameters: IFilterParameters
  ): Promise<IAverageSpendPerDuration[]> {
    try {
      console.log(`call to reportingService.getPerSpendDurationAverage`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/duration-spends?startDate=${filterParameters.fromDate}&endDate=${filterParameters.toDate}`;
      const avgSpendPerDuration = await axios.get<IAverageSpendPerDuration[]>(
        url,
        reqConfig
      );
      if (avgSpendPerDuration.data && avgSpendPerDuration.data.length > 0) {
        const result = avgSpendPerDuration.data.map(
          (item: IAverageSpendPerDuration) => {
            return {
              ...item,
              averageSpend: parseFloat((item.averageSpend / 100).toFixed(2)),
              averageSpendPerHead: parseFloat(
                (item.averageSpendPerHead / 100).toFixed(2)
              ),
              totalSpend: parseFloat((item.totalSpend / 100).toFixed(2)),
            } as IAverageSpendPerDuration;
          }
        );
        return result;
      }
    } catch (error) {
      console.warn(
        `call to reportingService.getPerSpendDurationAverage for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IAverageSpendPerDuration[];
  }

  /** Provides the items */
  static async getItemsByCustomerId(
    venueId: number,
    customerId: string
  ): Promise<IPurchasedItem[]> {
    try {
      console.log(`call to reportingService.getItemsByCustomerId`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const url = `${config.iPosServiceUrl}/internal/venues/${venueId}/reports/customer/${customerId}/Items`;
      const result = await axios.get<IPurchasedItem[]>(url, reqConfig);
      return result.data;
    } catch (error) {
      console.warn(
        `call to reportingService.getItemsByCustomerId for venue ${venueId} failed to load. ${error}`
      );
    }
    return [] as IPurchasedItem[];
  }

  static async getReportsDataForDownload(
    filterParameters: IFilterParameters,
    includeCancelledBookings: boolean
  ): Promise<IGuestsReportResult[]> {
    try {
      console.log(`call to reportingService.getReportsDataForDownload`);
      const reqConfig: AxiosRequestConfig = {
        headers: {
          ...headers.defaultHeaders(),
          ...headers.backendApiKeyHeader,
        },
      };

      const selectedVenueTimeZoneId = filterParameters.selectedVenue.timeZoneId;
      const startDateAsUtc = getUtcDateByVenueTimeZone(
        selectedVenueTimeZoneId,
        filterParameters.fromDate
      );
      const endDateAsUtc = getUtcDateByVenueTimeZone(
        selectedVenueTimeZoneId,
        filterParameters.toDate
      );
      const url = `${config.iPosServiceUrl}/internal/venues/${filterParameters.selectedVenue.id}/reports/guests/download?startDate=${startDateAsUtc}&endDate=${endDateAsUtc}&includeCustomerDetails=true&allowWalkins=false&includeCancelledBookings=${includeCancelledBookings}`;
      const result = await axios.get<IGuestsReportResult[]>(url, reqConfig);
      return result.data;
    } catch (error) {
      console.warn(
        `call to reportingService.getReportsDataForDownload for venue ${filterParameters.selectedVenue.id} failed to load. ${error}`
      );
    }
    return [] as IGuestsReportResult[];
  }
}
